import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Header from '../../main/components/Header';
import MainImageSlider from '../components/MainImageSlider';
import Description from '../components/Description';
import UserInfo from '../components/UserInfo';
import AuthorAds from '../components/AuthorAds';
import SimilarAds from '../components/SimilarAds';
import Path from '../../path/ui/Path';
import MobileBottomBar from '../../main/components/MobileBottomBar';

import getAnnouncementById from '../../../api/getAnnouncementById';
import '../style/main.css';
import CharacteristicsDisplay from '../components/CharacteristicsDisplay';
import eyeIcon from '../../../assets/icons/eye.svg';
import { trackEvent } from '../../../utils/AnalyticsUtils';
import DealerPage from '../components/DealerPage';
import MarketplaceDescription from '../components/MarketplaceDescription';
import { useProductSEO, useBreadcrumbSEO } from '../../../hooks/useSEO';

const Main = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [bottomBarHidden, setBottomBarHidden] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Scroll to top when announcement ID changes
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [id]);

    useEffect(() => {
        const fetchItem = async () => {
            try {
                const response = await getAnnouncementById(id);
                setItem(response.announcement);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchItem();
    }, [id]);

    useEffect(() => {
        if (item) {
            trackEvent('announcement_view', {
                announcement_id: item.id,
                announcement_title: item.title,
                views: item.views || 0,
            });
        }
    }, [item]);

    // Product SEO for announcement page
    useProductSEO(item ? {
        name: item.title,
        description: item.description?.substring(0, 160) || '',
        price: item.price,
        currency: item.currency || 'KZT',
        inStock: item.status === 'active',
        sellerName: item.seller?.name || item.seller?.company_name,
        images: item.image_urls || [],
        rating: item.rating ? { value: item.rating, count: item.review_count || 0 } : null
    } : null);

    // Breadcrumb SEO
    useBreadcrumbSEO(item ? [
        { name: 'Басты бет', url: 'https://agroland.kz/' },
        { name: item.category_name || 'Категория', url: `https://agroland.kz/category/${item.category_name}` },
        { name: item.title, url: `https://agroland.kz/announcement/${item.id}` }
    ] : [], [item]);

    if (loading) {
        return (
            <div className={`main_container ${isMobile ? 'mobile' : 'desktop'}`}>
                <Header onToggleMenu={() => {}} />
                <div className="main_content animate-pulse">
                    {/* Image area */}
                    <div className="flex-1 min-w-0">
                        <div className="w-full h-[400px] md:h-[320px] bg-gray-200 rounded-2xl" />
                        <div className="flex gap-2 mt-3">
                            {[1,2,3,4].map(i => (
                                <div key={i} className="w-16 h-16 bg-gray-200 rounded-xl flex-shrink-0" />
                            ))}
                        </div>
                    </div>
                    {/* Details area */}
                    <div className="main_details">
                        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
                        <div className="h-8 bg-gray-200 rounded w-1/2 mb-4" />
                        <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                        <div className="h-4 bg-gray-100 rounded w-5/6 mb-2" />
                        <div className="h-4 bg-gray-100 rounded w-4/6 mb-6" />
                        <div className="h-12 bg-gray-200 rounded-xl w-full mb-3" />
                        <div className="h-12 bg-gray-100 rounded-xl w-full mb-6" />
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" />
                            <div className="flex flex-col gap-2 flex-1">
                                <div className="h-4 bg-gray-200 rounded w-1/2" />
                                <div className="h-3 bg-gray-100 rounded w-1/3" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return <div>{t('error')}: {error}</div>;
    }

    if (!item) {
        return <div>{t('itemNotFound')}</div>;
    }

    const handleBottomBarToggle = (isHidden) => {
        setBottomBarHidden(isHidden);
    };

    return (
        <div className={`main_container ${isMobile ? 'mobile' : 'desktop'}`}>
            <Header onToggleMenu={handleBottomBarToggle} />
            <Path currentPath={item.title || t('unknownItem')} />

            {item.is_dealer || item.is_marketplace ? (
                /* ── Dealer/Marketplace: full rich layout ── */
                <div className="announcement_content">
                    <DealerPage item={item} isMobile={isMobile} />
                    {!bottomBarHidden && <MobileBottomBar activeTabName={'home'} />}
                </div>
            ) : (
                /* ── Regular announcement layout ── */
                <div className="announcement_content">
                    <div className="main_content">
                        <MainImageSlider
                            id={item.id}
                            images={Array.isArray(item.image_urls) && item.image_urls.length > 0 ? item.image_urls : []}
                            description={item.description || t('noDescription')}
                            views={item.views || 0}
                            videoUrl={item.video_url || null}
                            isMobile={isMobile}
                        />
                        <div className="main_details">
                            {item.is_marketplace ? (
                                <MarketplaceDescription
                                    id={item.id}
                                    title={item.title}
                                    date={item.created_at}
                                    price={item.price}
                                    price_type={item.price_type}
                                    is_negotiable={item.is_negotiable}
                                    barter={item.barter}
                                    description={item.description}
                                    rating={item.rating || ''}
                                    reviews={item.reviews || ''}
                                    user_id={item.seller?.id}
                                    is_favorite={item.is_favorite || false}
                                    stock_quantity={item.stock_quantity}
                                    price_min={item.price_min}
                                    min_order_quantity={item.min_order_quantity}
                                    can_deliver={item.can_deliver}
                                    currency={item.currency}
                                />
                            ) : (
                                <Description
                                    id={item.id}
                                    title={item.title}
                                    date={item.created_at}
                                    price={item.price}
                                    price_type={item.price_type}
                                    is_negotiable={item.is_negotiable}
                                    barter={item.barter}
                                    description={item.description}
                                    phone={Array.isArray(item.contact_numbers) && item.contact_numbers.length > 0
                                        ? item.contact_numbers[0].phone_number
                                        : 'No phone available'}
                                    rating={item.rating || ''}
                                    reviews={item.reviews || ''}
                                    user_id={item.seller?.id}
                                    is_favorite={item.is_favorite || false}
                                />
                            )}
                            {isMobile &&
                                <div className="ad_description">
                                    <h3>{t('description')}</h3>
                                    <p>{item.description || t('noDescription')}</p>
                                    <div className="ad_views">
                                        <div className='ad_views'>
                                            <img src={eyeIcon} alt='eye' />
                                            <span>{t('views')}: {item.views || 0}</span>
                                        </div>
                                        <span>ID: {item.id}</span>
                                    </div>
                                </div>
                            }
                            <UserInfo
                                user={item.seller}
                                locations={item.location}
                            />
                            <CharacteristicsDisplay
                                categoryId={item.category_id}
                                jsonData={item.json_data}
                            />
                        </div>
                    </div>
                    {Array.isArray(item.additional_announcements) && item.additional_announcements.length > 0 && (
                        <AuthorAds ads={item.additional_announcements} user={item.seller} />
                    )}
                    {Array.isArray(item.similar_announcements) && item.similar_announcements.length > 0 && (
                        <SimilarAds ads={item.similar_announcements} />
                    )}
                    {!bottomBarHidden && <MobileBottomBar activeTabName={'home'} />}
                </div>
            )}
        </div>
    );
};

export default Main;